import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { DirhamIconColored } from '../../components/DirhamIcon';
import CurrencyDisplay from '../../components/CurrencyDisplay';
import { 
  Mail, 
  Clock, 
  Send, 
  Calendar,
  TrendingUp,
  Users,
  FileText,
  RefreshCw,
  CheckCircle
} from 'lucide-react';
import { safeFormat } from '../../utils/dateUtils';

const DailyReport = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    send_time: '08:00',
    email: ''
  });
  const [todayReport, setTodayReport] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, reportRes, historyRes] = await Promise.all([
        api.get('/admin/daily-report-settings'),
        api.get('/admin/daily-report'),
        api.get('/admin/daily-reports-history?limit=7')
      ]);
      setSettings(settingsRes.data);
      setTodayReport(reportRes.data);
      setReportHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.post('/admin/daily-report-settings', settings);
      toast.success('تم حفظ الإعدادات بنجاح');
    } catch (error) {
      toast.error('فشل حفظ الإعدادات');
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await api.post('/admin/send-daily-report');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('فشل إرسال التقرير');
    } finally {
      setSending(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">التقرير اليومي</h1>
          <p className="text-muted-foreground">إعدادات وإرسال التقارير اليومية التلقائية</p>
        </div>
        <Button
          data-testid="send-report-now-btn"
          onClick={handleSendNow}
          disabled={sending}
          className="bg-primary text-primary-foreground gap-2"
        >
          {sending ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {sending ? 'جاري الإرسال...' : 'إرسال التقرير الآن'}
        </Button>
      </div>

      {/* Today's Summary */}
      {todayReport && (
        <div className="bg-card border border-white/5 rounded-xl p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            ملخص اليوم - {todayReport.date}
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center">
              <DirhamIconColored className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">الإيرادات</p>
              <p className="text-2xl font-bold text-green-500"><CurrencyDisplay amount={todayReport.total_revenue} className="text-green-500" /></p>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center">
              <TrendingUp className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">المصروفات</p>
              <p className="text-2xl font-bold text-red-500"><CurrencyDisplay amount={todayReport.total_expenses} className="text-red-500" /></p>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
              <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">العملاء</p>
              <p className="text-2xl font-bold text-blue-500">{todayReport.total_clients}</p>
            </div>
            
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 text-center">
              <FileText className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">صافي الربح</p>
              <p className={`text-2xl font-bold ${todayReport.net_profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                <CurrencyDisplay amount={todayReport.net_profit} className={todayReport.net_profit >= 0 ? 'text-green-500' : 'text-red-500'} />
              </p>
            </div>
          </div>

          {/* Barber Stats */}
          {todayReport.barber_stats?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold mb-3">أداء الحلاقين</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {todayReport.barber_stats.map((barber) => (
                  <div key={barber.id || barber.name} className="bg-secondary/30 rounded-lg p-3 flex items-center justify-between">
                    <span className="font-medium">{barber.name}</span>
                    <div className="text-end">
                      <p className="text-green-500 font-bold"><CurrencyDisplay amount={barber.revenue} className="text-green-500" /></p>
                      <p className="text-xs text-muted-foreground">{barber.clients} عميل</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings */}
      <div className="bg-card border border-white/5 rounded-xl p-6">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          إعدادات التقرير التلقائي
        </h2>

        <div className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
            <div>
              <p className="font-medium">تفعيل التقرير اليومي</p>
              <p className="text-sm text-muted-foreground">إرسال تقرير يومي تلقائي للمدير</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
            />
          </div>

          {/* Send Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                وقت الإرسال (UTC)
              </label>
              <Input
                type="time"
                value={settings.send_time}
                onChange={(e) => setSettings({ ...settings, send_time: e.target.value })}
                className="bg-secondary/50 border-white/10"
              />
              <p className="text-xs text-muted-foreground mt-1">
                الوقت بتوقيت UTC. توقيت الإمارات = UTC + 4 ساعات
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                البريد الإلكتروني
              </label>
              <Input
                type="email"
                value={settings.email || ''}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                placeholder="admin@example.com"
                className="bg-secondary/50 border-white/10"
              />
            </div>
          </div>

          <Button
            data-testid="save-settings-btn"
            onClick={handleSaveSettings}
            className="bg-primary text-primary-foreground"
          >
            <CheckCircle className="w-4 h-4 me-2" />
            حفظ الإعدادات
          </Button>
        </div>
      </div>

      {/* Report History */}
      {reportHistory.length > 0 && (
        <div className="bg-card border border-white/5 rounded-xl p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            سجل التقارير المرسلة
          </h2>
          
          <div className="space-y-3">
            {reportHistory.map((report) => (
              <div key={report.id || report.date} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium">{report.date}</p>
                    <p className="text-xs text-muted-foreground">
                      أُرسل في: {safeFormat(report.sent_at, 'yyyy-MM-dd HH:mm', { fallback: '-' })}
                    </p>
                  </div>
                </div>
                <div className="text-end">
                  <p className="text-green-500 font-bold"><CurrencyDisplay amount={report.data?.total_revenue} className="text-green-500" /></p>
                  <p className="text-xs text-muted-foreground">{report.data?.total_clients} عميل</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReport;
